import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'super_admin';
  avatar?: string;
  phone?: string;
  country?: string;
}

type LoginResult = { status: 'ok' } | { status: '2fa_required'; pendingToken: string };

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (pendingToken: string, code: string) => Promise<void>;
  register: (name: string, email: string, password: string, inviteToken?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configurar axios para incluir token automaticamente
// Em produção, usar caminho relativo para que o Nginx faça o proxy
// Em desenvolvimento, usar localhost
const getApiBaseURL = () => {
  // Se REACT_APP_API_URL estiver configurado, usar ele
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Produção com subpath /neia (ex.: ricci.solutions/neia)
  if (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '/') {
    return '/neia/api';
  }
  // Produção na raiz (ex.: europa.solutions) ou REACT_APP_API_URL já definido acima
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }

  return 'http://localhost:5000/api';
};

axios.defaults.baseURL = getApiBaseURL();

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de resposta: reage globalmente ao bloqueio por billing da plataforma (402).
// Usa CustomEvent (em vez de navegação direta) porque este código roda fora da árvore React.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 402 && error?.response?.data?.blocked === true) {
      window.dispatchEvent(new CustomEvent('billing:blocked', { detail: error.response.data }));
    }
    return Promise.reject(error);
  }
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get('/auth/me');
          setUser(response.data.user);
          setToken(storedToken);
        } catch (error) {
          // Só deslogar automaticamente se o backend retornar 401 (token inválido/expirado).
          // Em erros de rede ou outros códigos, mantemos o token e deixamos o usuário tentar de novo.
          const status = (error as any)?.response?.status;
          if (status === 401) {
            localStorage.removeItem('token');
            setToken(null);
          } else {
            console.error('Erro ao validar sessão em /auth/me, mantendo token local:', error);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      if (response.data.requires2FA) {
        return { status: '2fa_required', pendingToken: response.data.pendingToken };
      }
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { status: 'ok' };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer login');
    }
  };

  const verifyTwoFactor = async (pendingToken: string, code: string) => {
    try {
      const response = await axios.post('/auth/login/verify-2fa', { pendingToken, code });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Código inválido');
    }
  };

  const register = async (name: string, email: string, password: string, inviteToken?: string) => {
    try {
      const response = await axios.post('/auth/register', { name, email, password, ...(inviteToken ? { inviteToken } : {}) });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
    } catch (error: any) {
      const data = error.response?.data;
      const msg =
        data?.message ||
        (Array.isArray(data?.errors) && data.errors[0]?.msg) ||
        (error.response?.status === 403 ? 'Registro permitido apenas por convite' : null) ||
        'Erro ao registrar';
      throw new Error(msg);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        verifyTwoFactor,
        register,
        logout,
        loading,
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
